import { ArrowUpRight, Check, Calculator, GraduationCap, Laptop, Users } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";

export const TAXPRO_APPLY_URL =
  "https://forms.zohopublic.com/pts_portal/form/TopDollarTax41MEMBEROnboarding/formperma/1odYn-EHarj9hk8-T6Q84miwXCtrJT_B_zdVa6BIiEg";

const DUTIES = [
  "Prepare accurate individual and small-business tax returns for Heritage families",
  "Maximize every legal credit and deduction your clients qualify for",
  "Guide clients year-round — quarterly planning, W-4 reviews, and IRS letter support",
  "Build your own book of business with warm leads from the Heritage community",
  "Pair tax services with the Heritage protection conversation (insurance, legal, retirement)",
  "Serve clients remotely or in person — you set your schedule",
];

const PROVIDED = [
  "Complete onboarding and step-by-step training — no prior tax experience required",
  "Professional tax software, e-filing tools, and back-office support",
  "Mentorship from experienced tax professionals through our Top Dollar Tax partnership",
  "Marketing support and a trusted brand behind you",
  "Performance-based pay with unlimited earning potential during tax season and beyond",
];

const REQUIREMENTS = [
  "18 or older with a strong sense of integrity and confidentiality",
  "Willing to complete onboarding, training, and a background check",
  "Comfortable with basic technology and client conversations",
  "PTIN registration guidance provided during onboarding",
];

export default function TaxProPage() {
  return (
    <div data-testid="taxpro-page">
      {/* Sub-page hero image */}
      <section className="relative pt-24 overflow-hidden">
        <div className="relative">
          <img
            src="/careers/taxpro-banner.jpg"
            alt="Tax Professional career at Heritage Family Protection Agency"
            data-testid="taxpro-banner"
            className="w-full h-[42vh] md:h-[56vh] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#081226] via-[#081226]/35 to-[#081226]/20" />
          <div className="absolute bottom-0 left-0 right-0 pb-10">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <Reveal>
                <p className="font-body text-xs md:text-sm tracking-[0.3em] uppercase text-[#d4af37]">Careers · Path Five</p>
                <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white">
                  Become a <span className="text-gold-gradient italic">Tax Professional.</span>
                </h1>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-14 md:py-20" style={{ background: "radial-gradient(ellipse at 50% -10%, rgba(212,175,55,0.10), rgba(8,18,38,0) 55%)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="max-w-3xl font-body font-light text-base md:text-lg leading-relaxed text-white/65">
              Every family we protect files taxes — and most of them are leaving money on the table. As a Heritage Tax
              Professional, you help families and small businesses keep more of what they earn, file with confidence,
              and plan smarter all year long. Through our Top Dollar Tax onboarding, you get the training, software,
              and mentorship to earn serious income every tax season — no experience required to start.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <a
                href={TAXPRO_APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="taxpro-apply-btn"
                className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-9 py-4 font-body text-sm font-semibold tracking-wide text-black"
              >
                Apply Now <ArrowUpRight size={16} />
              </a>
              <p className="font-body text-xs text-white/40 max-w-sm leading-relaxed">
                Applications open in a secure onboarding form. Performance-based opportunity — earnings depend on effort,
                results, and applicable agreements.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              { icon: Calculator, title: "What you'll do", items: DUTIES },
              { icon: GraduationCap, title: "What we provide", items: PROVIDED },
              { icon: Users, title: "What it takes", items: REQUIREMENTS },
            ].map((col, ci) => (
              <Reveal key={col.title} delay={ci * 0.06}>
                <div className="h-full rounded-2xl border border-[#d4af37]/25 bg-[#0f2744]/70 p-7" data-testid={`taxpro-col-${ci}`}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-[#d4af37]/35 text-[#d4af37]">
                    <col.icon size={22} strokeWidth={1.5} />
                  </span>
                  <h2 className="mt-5 font-serif-display text-2xl text-white">{col.title}</h2>
                  <ul className="mt-5 space-y-3.5">
                    {col.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 font-body text-sm leading-relaxed text-white/70">
                        <Check size={16} className="mt-0.5 shrink-0 text-[#d4af37]" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.08}>
            <div className="mt-12 rounded-3xl border border-[#d4af37]/30 bg-gradient-to-br from-[#12284a] to-[#0a192f] p-10 md:p-14 text-center">
              <Laptop size={30} className="mx-auto text-[#d4af37]" strokeWidth={1.4} />
              <h2 className="mt-5 font-serif-display text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-white">
                Tax season is <span className="text-gold-gradient italic">opportunity season.</span>
              </h2>
              <p className="mt-4 mx-auto max-w-xl font-body font-light text-base md:text-lg text-white/60">
                One application starts your onboarding. Train now, be ready to earn when filing opens.
              </p>
              <a
                href={TAXPRO_APPLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="taxpro-apply-btn-bottom"
                className="btn-sweep mt-8 inline-flex items-center gap-2 rounded-full bg-gold-metallic px-9 py-4 font-body text-sm font-semibold tracking-wide text-black"
              >
                Apply Now — Start Onboarding <ArrowUpRight size={16} />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
